import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import * as Moment from 'moment';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import {
  getReportOpportunities,
} from 'redux/actions/reportActions';
import { get, isEmpty } from 'lodash';
import Table from 'components/tables';
import TableHead from 'components/tables/tableHead';
import TableSortLabel from 'components/tables/tableSortLabel';
import TableBody from 'components/tables/tableBody';
import TableCell from 'components/tables/tableCell';
import TableRow from 'components/tables/tableRow';
import NoResults from 'components/NoResults';
import Pagination from 'components/pagination/Pagination';
import { VIEW_OPPORTUNITIES_TAB_ANALYTICS } from 'constants/analytics';
import { getTimezoneAndUTCOffset } from 'utils/report';

import { analyticsTrack } from 'utils/analytics';
import { primary1000 } from 'themes/colors';
import { DATE_FORMAT_VERBOSE } from 'constants/config';
import { stripDecimalsAndFormat } from 'utils/misc';

const OpportunitiesTab = ({ report, getReportOpportunities: getReportOpportunitiesAction }) => {
  const { opportunities } = report;
  const [ordering, setOrdering] = useState(null);
  const [orderState, setOrderState] = useState('asc');

  useEffect(() => {
    analyticsTrack(VIEW_OPPORTUNITIES_TAB_ANALYTICS, {}, true);
  }, []);

  const calculateOrdering = (property, sorting) => {
    if (sorting === 'desc' && property) {
      return `-${property}`;
    }
    return property;
  };

  useEffect(() => {
    if (!isEmpty(report.reportParams)) {
      getReportOpportunitiesAction({
        ...report.reportParams,
        ...getTimezoneAndUTCOffset(),
        ordering: calculateOrdering(ordering, orderState),
      });
    }
  }, [report.reportParams]);

  const handleOrdering = (ord) => {
    const newOrderState = orderState === 'asc' ? 'desc' : 'asc';
    setOrderState(newOrderState);
    setOrdering(ord);
    getReportOpportunitiesAction({
      ...report.reportParams,
      ...getTimezoneAndUTCOffset(),
      ordering: calculateOrdering(ord, newOrderState),
    });
  };

  return (
    <div className="mb-4 overflow-auto">
      {(!report.loading && opportunities.results.length >= 1) &&
        <Table className="volt-table" noRadius>
          <TableHead>
            <TableRow>
              <TableCell onClick={() => handleOrdering('created_date')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={ordering}
                  property="created_date"
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => handleOrdering('title')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={ordering}
                  property="title"
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => handleOrdering('outlet.name')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={ordering}
                  property="outlet.name"
                >
                  Outlet
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => handleOrdering('contact_full_name')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={ordering}
                  property="contact_full_name"
                >
                  Contact
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => handleOrdering('stage')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={ordering}
                  property="stage"
                >
                  Stage
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => handleOrdering('reach')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={ordering}
                  property="reach"
                >
                  Potential Reach
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => handleOrdering('domain_authority')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={ordering}
                  property="domain_authority"
                >
                  DA
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => handleOrdering('uvpm')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={ordering}
                  property="uvpm"
                >
                  UVPM
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!isEmpty(opportunities.results) &&
              opportunities.results.map(opportunity => (
                <TableRow>
                  <TableCell>{Moment(opportunity.created_date).format(DATE_FORMAT_VERBOSE)}</TableCell>
                  <TableCell>{opportunity.title}</TableCell>
                  <TableCell>
                    {opportunity.outlet && !isEmpty(opportunity.outlet.website) ? (
                      <a
                        ref={(node) => {
                          if (node) {
                            node.style.setProperty('color', primary1000, 'important');
                          }
                        }}
                        href={`//${opportunity.outlet.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >{opportunity.outlet.name}
                      </a>
                    ) : (!isEmpty(opportunity.outlet) && opportunity.outlet.name)
                    }
                  </TableCell>
                  <TableCell>{opportunity.contact_full_name}</TableCell>
                  <TableCell>{opportunity.stage}</TableCell>
                  <TableCell>
                    {stripDecimalsAndFormat(get(opportunity, 'outlet.potential_reach'))}
                  </TableCell>
                  <TableCell>{get(opportunity, 'outlet.domain_authority')}</TableCell>
                  <TableCell>{get(opportunity, 'outlet.unique_monthly_visitors')}</TableCell>
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      }

      <NoResults
        loading={opportunities.loading}
        results={opportunities.results}
      />
      <Pagination
        items={opportunities}
        onChangePage={getReportOpportunitiesAction}
        params={report.reportParams}
      />
    </div>
  );
};

OpportunitiesTab.propTypes = {
  report: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    reportParams: PropTypes.shape({}).isRequired,
    opportunities: PropTypes.shape({
      results: PropTypes.shape({
        length: PropTypes.number.isRequired,
      }),
    }),
  }).isRequired,
  getReportOpportunities: PropTypes.func.isRequired,
};

export default compose(connect(
  state => ({
    report: state.report,
  }),
  { getReportOpportunities },
), withNamespaces('common'))(OpportunitiesTab);
